import { CPT } from '../patient-data';
import { Provider } from '../provider';

export interface ICD {
  name?: string;
  code?: string;
}

export interface Medication {
  basicInformation: {
    // The base name of the medication, excluding strength, quantity, and other details
    medicationName: string;
    // National Drug Code - unique identifier for this specific medication formulation
    ndcCode?: string;
  };
  dosage: {
    strength?: {
      // Amount or range of the active ingredient (e.g., 0.1 mg, 1 tablet, 2 mg/mL, 10 mg, 10 - 20 mg)
      value: string;
    };
    quantity?: {
      // Total amount prescribed
      value?: number;
      // Unit for the prescribed amount (e.g., tablets, capsules, mL)
      unit?: string;
    };
    form?: {
      unit: string;
    };
  };

  /***
   *
   * Coming soon 🔮
   * If you implement those fields in your adapter, please add them under FF and get Vim-OS approval before opening in prod
   *
   * TODO: make administration/timeline/documentation sub-entities required, with sub-fields optional
   *
   ***/
  administration?: {
    // How the medication should be administered (e.g., oral, nasal, transdermal)
    route?: string;
    // How often to take the medication (e.g., daily, twice a day, as needed)
    frequency?: string;
    // Special instructions for taking the medication (e.g., with food/milk, before/after meals)
    take?: string;
  };
  // When the medication should be taken
  timeline?: {
    // When the medication regimen begins (YYYY-MM-DD)
    startDate?: string;
    // When the medication regimen ends (YYYY-MM-DD)
    endDate?: string;
    // Number of days between start and end dates
    duration?: number;
    // Current state of the prescription (e.g., active, inactive, historical)
    status?: string;
  };
  // Additional clinical context and notes
  documentation?: {
    // Healthcare provider who prescribed or recorded the medication
    reportedBy?: string;
    // Additional instructions or comments about the medication
    notes?: string;
    // Original free-text description if medication was entered as unstructured data in EHR
    unstructured?: string;
  };
}

export interface LoincCode {
  // LOINC code (e.g. 2093-3, 2345-7)
  code: string;
  // Description of the LOINC code (e.g. "Cholesterol [Mass/volume] in Serum or Plasma", "Glucose [Mass/volume] in Serum or Plasma")
  description?: string;
}

export enum OrderType {
  LAB = 'LAB',
  DI = 'DI',
  PROCEDURE = 'PROCEDURE',
  RX = 'RX',
  REFERRAL = 'REFERRAL',
}

export interface OrderTargetProvider extends Omit<Provider, 'demographics' | 'type'> {
  demographics?: Provider['demographics'];
  type: 'FACILITY' | 'PROVIDER';
}

export interface BasicInformation {
  notes?: string;
  orderName?: string;
  reason?: string;
}
export interface Order {
  id?: string;
  type?: OrderType;
  icd: ICD[];
  cpts: CPT[];
  encounterId?: string;
  createdDate?: string; // YYYY-MM-DD
  orderingProvider?: Provider;
  medications?: Medication[];
  targetProvider?: OrderTargetProvider;
  basicInformation?: BasicInformation;
  loincCodes?: LoincCode[];
}
